"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, Tab, Tabs, Tooltip } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { TabContext, TabPanel } from "@mui/lab";
import dynamic from "next/dynamic";
import gaService from "@/api/services/ga.service";

import type { ChartData } from "@/components/chartRenderer";

const chartFallback = () => <div style={{ minHeight: 280 }} />;

const ChartRenderer = dynamic(() => import("@/components/chartRenderer"), {
  ssr: false,
  loading: chartFallback,
});

const CategoryWithBalls = dynamic(() => import("./CtegoryBalls"), {
  ssr: false,
  loading: chartFallback,
});

/**
 * Supported board kinds in your app that we need to render.
 */
type BoardKind =
  | "simple_bar_chart"
  | "horizontal_bar_chart"
  | "stacked_bar_chart"
  | "box_chart"
  | "GMV_box_chart"
  | "grouped_bar_chart"
  | "radial_graph"
  | "line_graph"
  | "category_data_balls"
  | "category_with_products_table"
  | "heat_map_with_data"
  | "category_data"
  | "pie_custom";

/** Utilities to detect / coerce API shapes into ChartData pieces */

// ordersOverTime / rtoOverTime style
function isXYSeries(obj: any): boolean {
  return (
    obj &&
    obj.xAxis &&
    Array.isArray(obj.xAxis.data) &&
    (Array.isArray(obj.yAxis) || (obj.yAxis && Array.isArray(obj.yAxis.data)))
  );
}

// aovDistribution style (single series yAxis)
function isSingleSeriesXY(obj: any): boolean {
  return obj && obj.xAxis && obj.yAxis && Array.isArray(obj.xAxis.data) && Array.isArray(obj.yAxis.data);
}

// heatmap style
function isHeatmapData(obj: any): boolean {
  return obj && Array.isArray(obj.xAxis) && Array.isArray(obj.yAxis) && Array.isArray(obj.data);
}

// pie/donut vector style
function isTitlesAndData(obj: any): boolean {
  return obj && Array.isArray(obj.titles) && Array.isArray(obj.data);
}

// list/table of objects
function isObjectArray(obj: any): boolean {
  return Array.isArray(obj) && (obj.length === 0 || typeof obj[0] === "object");
}

/** Map your legacy board `type` → ChartRenderer type */
function mapType(kind: BoardKind): ChartData["type"] {
  switch (kind) {
    case "line_graph":
      return "line";
    case "heat_map_with_data":
      return "heatmap";
    case "radial_graph":
    case "category_data_balls":
      return "donut"; // good visual fit for splits/share
    case "pie_custom":
      return "pie"
    case "category_with_products_table":
    case "category_data":
      return "table";
    // Horizontal, stacked, grouped, box tiles, GMV tiles → bar fallback
    case "horizontal_bar_chart":
      return "category"
    case "stacked_bar_chart":
    case "grouped_bar_chart":
    case "simple_bar_chart":
    case "box_chart":
    case "GMV_box_chart":
    default:
      return "bar";
  }
}

/** Coerce a raw API payload for a particular board kind into ChartData */
function toChartData(kind: BoardKind, raw: any, title?: string, yAxisUnit?: string): ChartData {
  const type = mapType(kind);
  if (isHeatmapData(raw)) {
    const yRows = (raw.yAxis as string[]).map((label: string, idx: number) => ({
      label,
      values: raw.data[idx] ?? [],
    }));
    return {
      title: title ?? "",
      type: "heatmap",
      x: { label: "Day", values: raw.xAxis },
      y: yRows,
    };
  }

  // 2) Multi-series XY (ordersOverTime, rtoOverTime)
  // {
  //   xAxis: { name, data: [...] },
  //   yAxis: [{ name, data: [...] }, ...]  OR { name, data: [...] }
  // }
  if (isXYSeries(raw)) {
    const seriesArray = Array.isArray(raw.yAxis) ? raw.yAxis : [raw.yAxis];
    return {
      title: title ?? "",
      type: type === "line" ? "line" : type,
      x: { label: raw.xAxis.name ?? "X", values: raw.xAxis.data ?? [] },
      y: seriesArray.map((s: any) => ({
        label: s.name ?? yAxisUnit ?? "Series",
        values: s.data ?? [],
      })),
    };
  }

  // 3) Single-series XY (aovDistribution)
  // {
  //   xAxis: { name, data: [...] },
  //   yAxis: { name, data: [...] }
  // }
  if (isSingleSeriesXY(raw)) {
    return {
      title: title ?? "",
      type: "bar",
      x: { label: raw.xAxis.name ?? "X", values: raw.xAxis.data ?? [] },
      y: [{ label: raw.yAxis.name ?? yAxisUnit ?? "Value", values: raw.yAxis.data ?? [] }],
    };
  }

  // 4) Titles/Data vectors (prepaidCodSplit, newVsRepeatBuyerSplit, buyerSegments)
  // { titles: [...], data: [...] }
  if (isTitlesAndData(raw)) {
    return {
      title: title ?? "",
      type: type === "donut" ? "donut" : "pie",
      x: { label: "Category", values: raw.titles },
      y: [{ label: yAxisUnit ?? "Share", values: raw.data }],
    };
  }

  // 5) Category lists (topParentCategories, topSubCategories, topProducts)
  // { data: [{ name, value, parentCategory? }, ...] }
  if (raw?.data && Array.isArray(raw.data)) {
    // Prefer a bar (clear rank) or donut (share view). We'll do bar for precision.
    const names = raw.data.map((d: any) => d.name ?? "");
    const values = raw.data.map((d: any) => d.value ?? 0);
    // If you want a table instead, swap to type: "table" and values: raw.data
    return {
      title: title ?? "",
      type: kind === "category_with_products_table" ? "table" : "bar",
      ...(kind === "category_with_products_table"
        ? { values: raw.data }
        : {
            x: { label: "Category", values: names },
            y: [{ label: yAxisUnit ?? "Value", values }],
          }),
    };
  }

  // 6) Metric tiles (discountedOrders / aovPercentage / rtoPercentage {value, difference}, gmvGenerated: number)
  if (typeof raw === "number" || (raw && typeof raw === "object" && "value" in raw)) {
    const val = typeof raw === "number" ? raw : Number(raw.value ?? 0);
    return {
      title: title ?? "",
      type: "bar",
      x: { label: "Metric", values: [title ?? "Current"] },
      y: [{ label: yAxisUnit ?? "Value", values: [val] }],
    };
  }

  // 7) Table array fallback
  if (isObjectArray(raw)) {
    return {
      title: title ?? "",
      type: "table",
      values: raw,
    };
  }

  // Default empty
  return {
    title: title ?? "",
    type: type,
    x: { label: "X", values: [] },
    y: [{ label: yAxisUnit ?? "Value", values: [] }],
  };
}

const Board: React.FC<any> = (props) => {
  const [screenSize, setScreenSize] = useState<"sm" | "md" | "lg" | "xl">("xl");
  const [viewMode, setViewMode] = useState(0);

  useEffect(() => {
    const width = window.innerWidth;
    setScreenSize(width < 600 ? "sm" : width < 960 ? "md" : width < 1280 ? "lg" : "xl");
  }, []);

  // Build chart data from the stats payload provided in props.data
  const chartForGraphView: ChartData = useMemo(
    () => toChartData(props.type as BoardKind, props.data, props.title, props.yAxisUnit),
    [props.type, props.data, props.title, props.yAxisUnit]
  );

  // For List View, if not already a table, derive a simple table from x/y
  const chartForListView: ChartData = useMemo(() => {
    if (chartForGraphView.type === "table") return chartForGraphView;
    const xs = chartForGraphView.x?.values ?? [];
    const ys = chartForGraphView.y ?? [];
    const rows: Record<string, unknown>[] = xs.map((cat, i) => {
      const row: Record<string, unknown> = { Label: cat };
      ys.forEach((s, idx) => (row[s.label || `Series ${idx + 1}`] = s.values[i] ?? 0));
      return row;
    });
    return { title: chartForGraphView.title, type: "table", values: rows };
  }, [chartForGraphView]);

  return (
    <Card className={props.className} sx={{
      border: "0.85px solid #3636364D",
      background:"#161616",
      overflow:"visible"
    }}>
      <CardContent>
        <div className="flex lg:items-center sm:items-start sx:items-start gap-4 lg:justify-between sm:flex-col xs:flex-col lg:flex-row md:justify-between sm:justify-items-start sx:justify-items-start">
          <span className="flex items-center md:gap-4 gap:2">
            {props.title && (
              <h1 className="lg:text-[18px] text-[17px] font-semibold lg:leading-[30px] leading-[24px] text-white">
                {props.title}
              </h1>
            )}
            <Tooltip
              title={props.description ? props.description : ""}
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#ffffff08",
                    color: "#ffffff",
                    border: "1px solid #6e5e7987",
                    padding: "14px",
                    lineHeight: "20px",
                    fontSize: "12px",
                    maxWidth: "350px",
                    backdropFilter: "blur(20px)",
                  },
                },
              }}
              enterTouchDelay={0}
            >
              <HelpOutlineIcon
                fontSize="small"
                sx={{
                  color: "#ffffff",
                  transition: "all 100ms",
                  "&:hover": { color: "#50DFB2" },
                }}
              />
            </Tooltip>
          </span>

          {!props.disableTab && (
            <Tabs value={viewMode} onChange={(_e, v) => setViewMode(v)}>
              <Tab label="Graph View" value={0} />
              <Tab label="List View" value={1} />
            </Tabs>
          )}
        </div>

        {props.disableTab ? (
          <div
            onMouseEnter={() => gaService.chartInteraction(props.title)}
            onTouchStart={() => gaService.chartInteraction(props.title)}
          >
            {props.type === "category_data_balls" ? <CategoryWithBalls data= {props.data} /> : <ChartRenderer data={chartForGraphView} showBg colorScheme="purple" showTitle={false} style={props.style}/>}
          </div>
        ) : (
          <TabContext value={viewMode.toString()} >
            <TabPanel
              value="0"
              sx={{ padding: screenSize === "sm" ? "0px" : "24px" }}
              onMouseEnter={() => gaService.chartInteraction(props.title)}
              onTouchStart={() => gaService.chartInteraction(props.title)}
            >

                {props.type === "category_data_balls" ? <CategoryWithBalls data= {props.data} /> : <ChartRenderer data={chartForGraphView} showBg colorScheme="purple" showTitle={false} style={props.style}/>}
              
            </TabPanel>
            <TabPanel value="1" sx={{ padding: screenSize === "sm" ? "0px" : "24px", overflow :"visble" }}>
              <ChartRenderer data={chartForListView} showBg colorScheme="purple" showTitle={false} style={props.style}/>
            </TabPanel>
          </TabContext>
        )}

        <p className="text-left text-[#737373] text-[12px]">{props.secondaryDescription} </p>
        {/* <GraphFeedback /> */}
      </CardContent>
    </Card>
  );
};

export default Board;
